// src/pages/analyzers/HomeImprovementAnalyzer.js (COMPLETE VERSION)
import { useState } from "react"
import { useLanguage } from "../../context/LanguageContext"
import { Link } from "react-router-dom"
import ReportDisplay from "../../components/ReportDisplay"
import { 
  Home, Phone, Target, RefreshCw, CheckCircle, TrendingUp, BookOpen, 
  Upload, FileAudio, AlertCircle, Download, Mail, ArrowLeft,
  Award, Clock, BarChart3, Users, Zap, Star
} from "lucide-react"

// Image Component with Fallback
const ImageWithFallback = ({ src, alt, className, fallbackContent }) => {
  const [imageError, setImageError] = useState(false)
  
  if (imageError) {
    return fallbackContent
  }
  
  return (
    <img 
      src={src}
      alt={alt}
      className={className}
      onError={() => setImageError(true)}
    />
  )
}

const HomeImprovementAnalyzer = () => {
  const [selectedFile, setSelectedFile] = useState(null)
  const [transcript, setTranscript] = useState("")
  const [analysis, setAnalysis] = useState("")
  const [loading, setLoading] = useState(false)
  const [agent, setAgent] = useState("")
  const [callType, setCallType] = useState("cold")
  const [error, setError] = useState("")
  const [showTips, setShowTips] = useState(false)
  const [tips, setTips] = useState(null)
  const [scriptTemplate, setScriptTemplate] = useState(null)
  const [showScript, setShowScript] = useState(false)
  const { language, t } = useLanguage()

  const getToken = () => {
    try {
      if (typeof window !== "undefined") return localStorage.getItem("token") || ""
    } catch (_) {}
    return ""
  }

  const callTypeInfo = {
    cold: { 
      icon: Phone, 
      color: "blue", 
      title: "Cold Call",
      titleEs: "Llamada en Frio",
      description: "First contact with new prospect",
      descriptionEs: "Primer contacto con nuevo prospecto",
      image: "/images/analyzers/home-improvement/cold-call.jpg",
      tips: ["Build rapport quickly", "Identify pain points", "Create urgency"],
      gradient: "from-blue-500 to-cyan-500"
    },
    warm: { 
      icon: Target, 
      color: "orange", 
      title: "Warm Lead", 
      description: "Inbound lead actively searching",
      image: "/images/analyzers/home-improvement/warm-lead.jpg",
      tips: ["Acknowledge their interest", "Qualify budget", "Schedule quickly"],
      gradient: "from-orange-500 to-amber-500"
    },
    rehash: { 
      icon: RefreshCw, 
      color: "purple", 
      title: "Rehash Call", 
      description: "Re-engage previous quote",
      image: "/images/analyzers/home-improvement/rehash-call.jpg",
      tips: ["Address objections", "Offer new incentives", "Create FOMO"],
      gradient: "from-purple-500 to-pink-500"
    },
    confirmation: { 
      icon: CheckCircle, 
      color: "green", 
      title: "Confirmation", 
      description: "Confirm appointment details",
      image: "/images/analyzers/home-improvement/confirmation.jpg",
      tips: ["Confirm all details", "Build excitement", "Prevent no-shows"],
      gradient: "from-green-500 to-emerald-500"
    }
  }

  const industryStats = [
    { label: "Calls Analyzed", value: "5,000+", icon: Phone },
    { label: "Avg Improvement", value: "52%", icon: TrendingUp },
    { label: "Active Agents", value: "150+", icon: Users },
    { label: "Success Rate", value: "78%", icon: Award }
  ]

  const handleFileChange = (event) => {
    const file = event.target.files[0]
    if (file) {
      setTranscript("")
      setAnalysis("")
      setError("")
      setSelectedFile(file)
    }
  }

  const authHeaders = () => {
    const t = getToken()
    return t ? { Authorization: `Bearer ${t}` } : {}
  }

  const fetchCoachingTips = async (type) => {
    try {
      const response = await fetch(`https://call-analyzer-backend.onrender.com/coaching-tips/${type}`, {
        headers: authHeaders(),
      })
      if (!response.ok) throw new Error("Failed to fetch tips")
      const data = await response.json()
      setTips(data)
      setShowTips(true)
    } catch (err) {
      setError(err.message || "Failed to fetch tips")
    }
  }

  const fetchScriptTemplate = async (type) => {
    try {
      const response = await fetch(`https://call-analyzer-backend.onrender.com/script-templates/${type}`, {
        headers: authHeaders(),
      })
      if (!response.ok) throw new Error("Failed to fetch script template")
      const data = await response.json()
      setScriptTemplate(data)
      setShowScript(true)
    } catch (err) {
      setError(err.message || "Failed to fetch script")
    }
  }

  const handleUpload = async () => {
    if (!selectedFile || !agent.trim()) {
      setError("Please select a file and enter the agent name.")
      return
    }
    if (!getToken()) {
      setError("You are not logged in. Please log in to continue.")
      return
    }

    setLoading(true)
    setError("")
    const formData = new FormData()
    formData.append("file", selectedFile)
    formData.append("type", callType)
    formData.append("agent", agent)
    formData.append("industry", "home_improvement")
    formData.append("language", language === "es" ? "es" : "en")  // Pass language for transcription

    try {
      const response = await fetch("https://call-analyzer-backend.onrender.com/analyze", {
        method: "POST",
        headers: authHeaders(),
        body: formData,
      })

      if (response.status === 402) {
        setError("Subscription required. Redirecting to pricing...")
        setTimeout(() => {
          window.location.href = "/pricing"
        }, 2000)
        return
      }
      if (response.status === 429) {
        setError("Monthly limit reached. Please upgrade for more analyses.")
        setTimeout(() => {
          window.location.href = "/pricing"
        }, 2000)
        return
      }

      const data = await response.json()
      if (!response.ok) throw new Error(data.detail || "Analysis failed")

      setTranscript(data.transcript || "")
      setAnalysis(data.analysis || "")
    } catch (err) {
      setError(`Upload failed: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleEmailReport = async () => {
    const emailAddress = prompt("Enter email address to send the report:")
    if (!emailAddress) return

    try {
      const response = await fetch("https://call-analyzer-backend.onrender.com/send-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders()
        },
        body: JSON.stringify({
          email: emailAddress,
          transcript: transcript,
          analysis: analysis
        })
      })

      if (response.ok) {
        alert("Report sent successfully!")
      } else {
        alert("Failed to send report. Please try again.")
      }
    } catch (err) {
      alert("Error sending report: " + err.message)
    }
  }

  const handleNewAnalysis = () => {
    setSelectedFile(null)
    setTranscript("")
    setAnalysis("")
    setAgent("")
    setCallType("cold")
    setError("")
  }

  const reportReady = !!analysis

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-emerald-50">
      {/* Hero Header with Background Image */}
      <div className="relative bg-gradient-to-r from-emerald-600 to-teal-600 text-white overflow-hidden">
        {/* Background Image with Fallback */}
        <div className="absolute inset-0 z-0">
          <ImageWithFallback
            src="/images/analyzers/home-improvement/hero-bg.jpg"
            alt="Home Improvement"
            className="w-full h-full object-cover opacity-20"
            fallbackContent={
              <div className="w-full h-full bg-gradient-to-br from-emerald-700 to-teal-700 opacity-30"></div>
            }
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Home className="h-10 w-10 mr-4" />
              <div>
                <h1 className="text-3xl font-bold">{language === "es" ? "Analizador de Llamadas - Mejoras del Hogar" : "Home Improvement Call Analyzer"}</h1>
                <p className="text-emerald-100 mt-1">{language === "es" ? "Construccion, Renovacion y Servicios del Hogar" : "Construction, Renovation & Home Services"}</p>
              </div>
            </div>
            <Link to="/analyzer" className="flex items-center text-white hover:text-emerald-100 transition">
              <ArrowLeft className="h-5 w-5 mr-1" />
              All Analyzers
            </Link>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            {industryStats.map((stat, index) => (
              <div key={index} className="bg-white/10 backdrop-blur rounded-lg p-4 text-center">
                <stat.icon className="h-6 w-6 mx-auto mb-2 text-emerald-200" />
                <div className="text-2xl font-bold">{stat.value}</div>
                <div className="text-sm text-emerald-100">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {!reportReady ? (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Sidebar - Upload Form */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-lg p-6 sticky top-4">
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                  <Upload className="h-5 w-5 mr-2 text-emerald-600" />
                  {language === "es" ? "Subir Grabacion de Llamada" : "Upload Call Recording"}
                </h2>

                {/* Agent Name */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {language === "es" ? "Nombre del Agente" : "Agent Name"}
                  </label>
                  <input
                    type="text"
                    placeholder={language === "es" ? "Ingrese nombre del agente" : "Enter agent name"}
                    value={agent}
                    onChange={(e) => setAgent(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>

                {/* File Upload */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {language === "es" ? "Archivo de Audio" : "Audio File"}
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-emerald-500 transition">
                    <FileAudio className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <input
                      type="file"
                      onChange={handleFileChange}
                      accept=".wav,.mp3,.m4a"
                      className="hidden"
                      id="file-upload"
                    />
                    <label
                      htmlFor="file-upload"
                      className="cursor-pointer text-emerald-600 hover:text-emerald-700 font-medium"
                    >
                      {language === "es" ? "Seleccionar archivo o arrastra aqui" : "Choose file or drag & drop"}
                    </label>
                    {selectedFile && (
                      <div className="mt-3 text-sm text-gray-600">
                        <p className="font-medium">{selectedFile.name}</p>
                        <p className="text-xs">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
                    <AlertCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                {/* Analyze Button */}
                <button
                  onClick={handleUpload}
                  disabled={loading || !selectedFile || !agent}
                  className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                      {language === "es" ? "Analizando Llamada..." : "Analyzing Call..."}
                    </span>
                  ) : (
                    <span className="flex items-center justify-center">
                      <Zap className="h-5 w-5 mr-2" />
                      {language === "es" ? "Analizar Llamada" : "Analyze Call"}
                    </span>
                  )}
                </button>

                {/* Quick Tips */}
                <div className="mt-6 p-4 bg-emerald-50 rounded-lg">
                  <h3 className="font-semibold text-emerald-900 mb-2">{language === "es" ? "Consejos Rapidos" : "Quick Tips"}</h3>
                  <ul className="text-sm text-emerald-700 space-y-1">
                    <li>• Max file size: 50MB</li>
                    <li>• Supported: MP3, WAV, M4A</li>
                    <li>• Best quality: Clear audio</li>
                    <li>• Duration: Up to 60 minutes</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Main Content Area */}
            <div className="lg:col-span-2">
              {/* Call Type Selection with Images */}
              <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6">{language === "es" ? "Seleccionar Tipo de Llamada" : "Select Call Type"}</h2>
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(callTypeInfo).map(([type, info]) => {
                    const Icon = info.icon
                    const isSelected = callType === type
                    return (
                      <button
                        key={type}
                        onClick={() => setCallType(type)}
                        className={`relative rounded-xl border-2 transition-all overflow-hidden ${
                          isSelected 
                            ? "border-emerald-500 shadow-lg scale-105" 
                            : "border-gray-200 hover:border-emerald-300 hover:shadow-md"
                        }`}
                      >
                        {/* Call Type Image */}
                        <div className="h-32 relative overflow-hidden">
                          <ImageWithFallback
                            src={info.image}
                            alt={info.title}
                            className="w-full h-full object-cover"
                            fallbackContent={
                              <div className={`w-full h-full bg-gradient-to-br ${info.gradient} opacity-80 flex items-center justify-center`}>
                                <Icon className="h-12 w-12 text-white opacity-70" />
                              </div>
                            }
                          />
                          {/* Overlay gradient */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                          
                          {/* Selected checkmark */}
                          {isSelected && (
                            <div className="absolute top-2 right-2 bg-emerald-500 text-white p-1 rounded-full">
                              <CheckCircle className="h-5 w-5" />
                            </div>
                          )}
                        </div>

                        {/* Card Content */}
                        <div className="p-4 bg-white">
                          <div className="flex flex-col items-center">
                            <Icon className={`h-8 w-8 text-${info.color}-600 mb-2`} />
                            <h3 className="font-semibold text-gray-900">{info.title}</h3>
                            <p className="text-xs text-gray-600 mt-1">{info.description}</p>
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Resources Section with Gradient Backgrounds */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6">{language === "es" ? "Recursos y Entrenamiento" : "Resources & Training"}</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <button 
                    onClick={() => fetchCoachingTips(callType)} 
                    className="relative overflow-hidden p-6 rounded-lg hover:shadow-md transition group"
                  >
                    {/* Background Image or Gradient */}
                    <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 to-purple-50 opacity-90"></div>
                    
                    <div className="relative z-10">
                      <TrendingUp className="h-8 w-8 text-indigo-600 mb-3" />
                      <h3 className="font-semibold text-gray-900 mb-1">Coaching Tips</h3>
                      <p className="text-sm text-gray-600">Get expert tips for {callTypeInfo[callType].title.toLowerCase()} calls</p>
                      
                      {/* Hover effect */}
                      <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition">
                        <ArrowLeft className="h-5 w-5 text-indigo-600 rotate-180" />
                      </div>
                    </div>
                  </button>

                  <button 
                    onClick={() => fetchScriptTemplate(callType)} 
                    className="relative overflow-hidden p-6 rounded-lg hover:shadow-md transition group"
                  >
                    {/* Background Image or Gradient */}
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-50 to-pink-50 opacity-90"></div>
                    
                    <div className="relative z-10">
                      <BookOpen className="h-8 w-8 text-purple-600 mb-3" />
                      <h3 className="font-semibold text-gray-900 mb-1">Script Templates</h3>
                      <p className="text-sm text-gray-600">Professional scripts for {callTypeInfo[callType].title.toLowerCase()}</p>
                      
                      {/* Hover effect */}
                      <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition">
                        <ArrowLeft className="h-5 w-5 text-purple-600 rotate-180" />
                      </div>
                    </div>
                  </button>
                </div>

                {/* Additional Resources Grid */}
                <div className="mt-6 grid grid-cols-3 gap-4">
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 text-center">
                    <Award className="h-6 w-6 text-emerald-600 mx-auto mb-2" />
                    <p className="text-xs font-medium text-gray-700">Best Practices</p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-4 text-center">
                    <BarChart3 className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                    <p className="text-xs font-medium text-gray-700">Performance Metrics</p>
                  </div>
                  <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg p-4 text-center">
                    <Star className="h-6 w-6 text-yellow-600 mx-auto mb-2" />
                    <p className="text-xs font-medium text-gray-700">Top Performers</p>
                  </div>
                </div>
              </div>

              {/* Industry Insights Card */}
              <div className="mt-6 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl shadow-lg p-6 text-white">
                <h3 className="text-lg font-bold mb-3">Industry Insights</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-3xl font-bold">87%</p>
                    <p className="text-sm text-emerald-100">Close rate improvement</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold">2.5x</p>
                    <p className="text-sm text-emerald-100">More appointments set</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Results View - Using Professional Report Display
          <ReportDisplay
            analysis={analysis}
            transcript={transcript}
            agent={agent}
            callType={callType}
            industry="home_improvement"
            onEmailReport={handleEmailReport}
            onNewAnalysis={handleNewAnalysis}
          />
        )}
      </div>

      {/* Tips Modal */}
      {showTips && tips && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-6">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-bold text-gray-900">{tips.title}</h3>
                <button 
                  onClick={() => setShowTips(false)} 
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="p-6">
              <ul className="space-y-4">
                {tips.tips.map((tip, index) => (
                  <li key={index} className="flex items-start">
                    <span className="bg-emerald-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm mr-3 flex-shrink-0">
                      {index + 1}
                    </span>
                    <span className="text-gray-700">{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Script Modal */}
      {showScript && scriptTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-6">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-bold text-gray-900">Script Template</h3>
                <button 
                  onClick={() => setShowScript(false)} 
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Opening</h4>
                <div className="bg-emerald-50 p-4 rounded-lg">
                  <p className="text-gray-700">{scriptTemplate.opening}</p>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Value Proposition</h4>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-gray-700">{scriptTemplate.value_prop}</p>
                </div>
              </div>
              {scriptTemplate.rebuttals && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Common Rebuttals</h4>
                  {Object.entries(scriptTemplate.rebuttals).map(([objection, rebuttal]) => (
                    <div key={objection} className="mb-4">
                      <div className="bg-red-50 p-3 rounded-lg mb-2">
                        <p className="text-sm font-medium text-red-900">
                          Customer: "{objection.replace(/_/g, " ")}"
                        </p>
                      </div>
                      <div className="bg-green-50 p-3 rounded-lg">
                        <p className="text-sm text-green-900">{rebuttal}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {scriptTemplate.closing && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Closing</h4>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-gray-700">{scriptTemplate.closing}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default HomeImprovementAnalyzer
